import { apiRequest, apiRequestWithPagination } from '../utils/api';
import { getAdminToken } from '../utils/auth';
import { API_BASE_URL } from '@/config/api';

// Page settings endpoint (without base URL, since apiRequest adds it)
const BASE = '/page-settings';

export type LocaleText = { en?: string; per?: string; ps?: string }
export type ImageMeta = {
  url: string
  filename?: string
  originalName?: string
  size?: number
  mimetype?: string
  title?: LocaleText
  description?: LocaleText
}

export type PageSettings = {
  _id?: string
  pageName: string
  heroImages?: (string | ImageMeta)[]
  heroImage?: string | null
  bodyImage?: string | ImageMeta | null
  title?: LocaleText
  description?: LocaleText
  isActive?: boolean
  createdAt?: string
  updatedAt?: string
  // Homepage intro section
  introTitle?: LocaleText
  introDescription?: LocaleText
  introPrimaryButtonText?: LocaleText
  introPrimaryButtonLink?: string
  introSecondaryButtonText?: LocaleText
  introSecondaryButtonLink?: string
}

export const pageSettingsService = {
  list: async (): Promise<any> => {
    return apiRequestWithPagination(`${BASE}`, {})
  },

  get: async (pageName: string): Promise<any> => {
    return apiRequest(`${BASE}/${encodeURIComponent(pageName)}`, { method: 'GET' })
  },

  upsert: async (data: {
    pageName: string
    title?: LocaleText
    description?: LocaleText
    isActive?: boolean
    replaceHeroImages?: boolean
    heroImages?: (string | ImageMeta)[]
    bodyImage?: string | ImageMeta | null
    heroFiles?: File[]
    bodyFile?: File | null
    // Homepage intro section
    introTitle?: LocaleText
    introDescription?: LocaleText
    introPrimaryButtonText?: LocaleText
    introPrimaryButtonLink?: string
    introSecondaryButtonText?: LocaleText
    introSecondaryButtonLink?: string
  }): Promise<any> => {
    const fd = new FormData()
    const { heroFiles, bodyFile, ...rest } = data
    fd.append('data', JSON.stringify(rest))
    if (heroFiles && heroFiles.length) {
      for (const file of heroFiles) fd.append('images', file)
    }
    if (bodyFile) fd.append('bodyImage', bodyFile)

    // Use a raw fetch to avoid forcing application/json header
    const token = getAdminToken()
    const apiBase = (import.meta.env.VITE_ADMIN_API_BASE as string) || API_BASE_URL
    const res = await fetch(`${apiBase}/page-settings`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: fd,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to save page settings')
    }
    return res.json()
  },

  updateHero: async (pageName: string, data: {
    heroImages?: (string | ImageMeta)[]
    imageMetadata?: { title?: LocaleText; description?: LocaleText }[]
    heroFiles?: File[]
  }): Promise<any> => {
    const fd = new FormData()
    const { heroFiles, ...rest } = data
    fd.append('data', JSON.stringify(rest))
    if (heroFiles && heroFiles.length) {
      for (const file of heroFiles) fd.append('images', file)
    }
    const token = getAdminToken()
    const apiBase = (import.meta.env.VITE_ADMIN_API_BASE as string) || API_BASE_URL
    const res = await fetch(`${apiBase}/page-settings/${encodeURIComponent(pageName)}/hero-image`, {
      method: 'PUT',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: fd,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to update hero images')
    }
    return res.json()
  },

  delete: async (id: string): Promise<any> => {
    const token = getAdminToken()
    const apiBase = (import.meta.env.VITE_ADMIN_API_BASE as string) || API_BASE_URL
    const res = await fetch(`${apiBase}/page-settings/${id}`, {
      method: 'DELETE',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to delete page settings')
    }
    return res.json()
  },

  updateBody: async (pageName: string, bodyFile?: File): Promise<any> => {
    const fd = new FormData()
    if (bodyFile) fd.append('bodyImage', bodyFile)
    const token = getAdminToken()
    const apiBase = (import.meta.env.VITE_ADMIN_API_BASE as string) || API_BASE_URL
    const res = await fetch(`${apiBase}/page-settings/${encodeURIComponent(pageName)}/body-image`, {
      method: 'PUT',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: fd,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to update body image')
    }
    return res.json()
  },

  remove: async (pageName: string): Promise<any> => {
    return apiRequest(`${BASE}/${encodeURIComponent(pageName)}`, { method: 'DELETE' })
  },
}
